// Shared date utilities used by Allocation, Projections, and Channel Intelligence pages.
// All helpers operate in LOCAL time — never UTC — so date inputs (<input type="date">)
// never drift by a day across timezones.

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_MAP = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

// ISO YYYY-MM-DD for a specific local date.
export const toISO = (d) => {
  const tz = d.getTimezoneOffset();
  return new Date(d.getTime() - tz * 60_000).toISOString().slice(0, 10);
};

// ISO YYYY-MM-DD for "today" in local time.
export const todayISO = () => toISO(new Date());

// ISO YYYY-MM-DD for the first of the current month.
export const firstOfMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// Parse YYYY-MM-DD as a LOCAL date (avoids tz drift on date inputs).
export const parseLocalDate = (iso) => {
  if (!iso) return null;
  const [y, m, day] = iso.split('-').map(Number);
  return new Date(y, m - 1, day);
};

// Add `days` to a Date (returns new Date).
export const addDays = (d, days) => {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
};

// Monday of the week containing `d` (local time, midnight).
export const mondayOf = (d) => {
  const out = new Date(d);
  const day = out.getDay(); // 0=Sun..6=Sat
  const offset = (day + 6) % 7; // 0 if Mon, 1 if Tue, ..., 6 if Sun
  out.setDate(out.getDate() - offset);
  out.setHours(0, 0, 0, 0);
  return out;
};

// Permissive date parser — accepts "DD/MM/YYYY", "DD-MM-YYYY", "D MMM YYYY", or ISO.
// Returns null on failure. ALL outputs are local-time Dates so range comparisons
// against parseLocalDate(<input type=date>) never drift by a day.
export const parseAnyDate = (s) => {
  if (!s) return null;
  const str = s.toString().trim();
  if (!str) return null;
  // ISO YYYY-MM-DD (optionally with time) — parse the date portion as LOCAL,
  // never UTC. `new Date("2026-05-01")` is UTC midnight, which in US timezones
  // maps to Apr 30 local and silently drops that day from any range comparison.
  const isoMatch = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    const [, y, m, day] = isoMatch;
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(day, 10));
    return isNaN(d.getTime()) ? null : d;
  }
  // DD/MM/YYYY or DD-MM-YYYY
  const slash = str.split(/[/\-]/);
  if (slash.length === 3 && /^\d+$/.test(slash[0])) {
    const [dd, mm, yyyy] = slash;
    const y = yyyy.length === 2 ? 2000 + parseInt(yyyy, 10) : parseInt(yyyy, 10);
    const d = new Date(y, parseInt(mm, 10) - 1, parseInt(dd, 10));
    return isNaN(d.getTime()) ? null : d;
  }
  // "D MMM YYYY"
  const spaced = str.split(/\s+/);
  if (spaced.length === 3) {
    const dd = parseInt(spaced[0], 10);
    const m = MONTH_MAP[spaced[1].slice(0, 3).toLowerCase()];
    const y = parseInt(spaced[2], 10);
    if (!isNaN(dd) && m != null && !isNaN(y)) {
      const d = new Date(y < 100 ? 2000 + y : y, m, dd);
      return isNaN(d.getTime()) ? null : d;
    }
  }
  // Last-resort fallback
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

// Calendar-based weeks Mon→Sun within a month. Days before the first Monday belong
// to the previous month's last week — excluded here.
export const weekBucketsForMonth = (monthIdx, year) => {
  const lastDay = new Date(year, monthIdx + 1, 0).getDate();
  const out = [];
  let d = 1;
  while (new Date(year, monthIdx, d).getDay() !== 1 && d <= lastDay) d += 1;
  let n = 1;
  const monthNameShort = new Date(year, monthIdx, 1).toLocaleString('en-US', { month: 'short' });
  while (d <= lastDay) {
    const endDay = Math.min(d + 6, lastDay);
    out.push({
      key: `Week ${n}`,
      label: `Week ${n} (${monthNameShort} ${d}–${monthNameShort} ${endDay})`,
      start: new Date(year, monthIdx, d),
      end: new Date(year, monthIdx, endDay),
    });
    n += 1;
    d += 7;
  }
  return out;
};

// For an arbitrary date, return the index of the bucket containing it (0-based).
// Clamps to 0 if the date predates the first Monday of its month.
export const weekIndexForDate = (d) => {
  const buckets = weekBucketsForMonth(d.getMonth(), d.getFullYear());
  for (let i = 0; i < buckets.length; i++) {
    if (d >= buckets[i].start && d <= buckets[i].end) return i;
  }
  return 0;
};
